import { useEffect, useRef, useState } from 'react';

import { Audit } from 'models/Audit';
import { JobMovementEngineerInfo } from 'models/Engineers';
import { fetchEngineers } from 'store/endpoints/engineers';
import { commonErrorHandling, QueryParams } from 'store/utils';
import { onlyUnique } from 'utils';

function jobMovementEngineerIds(auditData: Audit[]): string[] {
  return onlyUnique(
    auditData
      .filter(
        ({ changedField, additionalData }) =>
          changedField === 'scheduleId' && additionalData !== null
      )
      .map(({ additionalData }) => additionalData)
  );
}

export interface JobMovementEngineersState {
  engineers?: JobMovementEngineerInfo[];
  isLoading: boolean;
}

export function useJobMovementEngineers(
  auditData: Audit[]
): JobMovementEngineersState {
  const [engineers, setEngineers] = useState<JobMovementEngineerInfo[]>();
  const [isLoading, setIsLoading] = useState(
    () => jobMovementEngineerIds(auditData).length > 0
  );

  const isMounted = useRef(true);

  useEffect(() => {
    const engineerIds = jobMovementEngineerIds(auditData);

    if (engineerIds.length === 0) {
      return undefined;
    }

    const abortController = new AbortController();

    (async () => {
      const fetchAuditEngineersQuery: QueryParams = {
        id: {
          values: engineerIds,
          operator: '='
        }
      };

      try {
        const engineersResponse = await fetchEngineers(
          fetchAuditEngineersQuery,
          abortController.signal
        );

        if (isMounted.current) {
          setEngineers(
            engineersResponse.data.items.map(({ id, firstName, lastName }) => ({
              id,
              firstName,
              lastName
            }))
          );
        }
      } catch (error) {
        commonErrorHandling(error);
      } finally {
        if (isMounted.current) {
          setIsLoading(false);
        }
      }
    })();

    return () => {
      abortController.abort();
      isMounted.current = false;
    };
  }, []);

  return { engineers, isLoading };
}
